import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  Group,
  Loader,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconDeviceFloppy, IconInfoCircle, IconShieldLock } from '@tabler/icons-react';
import { expeditorProfileSchema, type ExpeditorProfileDto } from '@expeditor/shared';
import { expeditorProfileApi } from '../../shared/api/expeditor-profile';
import {
  AddressBreakdownFields,
  useAddressBreakdown,
} from '../../shared/ui/AddressBreakdownFields';
import { useAuthStore } from '../../shared/auth/auth.store';

type FormState = Record<
  keyof Omit<
    ExpeditorProfileDto,
    'updatedAt' | 'updatedByUserEmail' | 'etrnOneContainerPerDoc'
  >,
  string
>;

const emptyForm: FormState = {
  fullName: '',
  shortName: '',
  inn: '',
  ogrn: '',
  kpp: '',
  legalAddress: '',
  postalAddress: '',
  contactEmail: '',
  contactPhone: '',
  directorName: '',
  directorPosition: '',
  bankName: '',
  bankBik: '',
  bankCorrAccount: '',
  bankAccount: '',
  edoSubscriberId: '',
};

function dtoToForm(dto: ExpeditorProfileDto): FormState {
  return {
    fullName: dto.fullName ?? '',
    shortName: dto.shortName ?? '',
    inn: dto.inn ?? '',
    ogrn: dto.ogrn ?? '',
    kpp: dto.kpp ?? '',
    legalAddress: dto.legalAddress ?? '',
    postalAddress: dto.postalAddress ?? '',
    contactEmail: dto.contactEmail ?? '',
    contactPhone: dto.contactPhone ?? '',
    directorName: dto.directorName ?? '',
    directorPosition: dto.directorPosition ?? '',
    bankName: dto.bankName ?? '',
    bankBik: dto.bankBik ?? '',
    bankCorrAccount: dto.bankCorrAccount ?? '',
    bankAccount: dto.bankAccount ?? '',
    edoSubscriberId: dto.edoSubscriberId ?? '',
  };
}

export function ExpeditorProfilePage() {
  const user = useAuthStore((s) => s.user);
  const canEdit = user?.role === 'ADMIN';

  const [dto, setDto] = useState<ExpeditorProfileDto | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  // Настройка ЭТрН «один контейнер на документ» — булев флаг, отдельно от form.
  const [oneContainer, setOneContainer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Разбивка юридического адреса (АдрРФ) — нужна, когда мы сторона ЭТрН.
  const { parts: addrParts, setParts: setAddrParts, save: saveAddrParts } = useAddressBreakdown(
    dto?.legalAddress,
  );

  useEffect(() => {
    let cancelled = false;
    expeditorProfileApi
      .get()
      .then((d) => {
        if (cancelled) return;
        setDto(d);
        setForm(dtoToForm(d));
        setOneContainer(d.etrnOneContainerPerDoc);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Не удалось загрузить'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const set = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    // Захватываем значение синхронно: внутри updater'а setForm((f) => ...)
    // React успевает очистить SyntheticEvent, и e.currentTarget становится null.
    const value = e.currentTarget.value;
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    const payload = { ...form, etrnOneContainerPerDoc: oneContainer };
    const parsed = expeditorProfileSchema.safeParse(payload);
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '));
      return;
    }
    setSaving(true);
    try {
      const updated = await expeditorProfileApi.update(payload);
      // Разбивка хранится отдельно, по тексту адреса.
      await saveAddrParts(form.legalAddress).catch(() => undefined);
      setDto(updated);
      setForm(dtoToForm(updated));
      setOneContainer(updated.etrnOneContainerPerDoc);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  const fields = (
    <>
      <Title order={5}>Общие сведения</Title>
      <Grid>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <TextInput
            label="Полное наименование"
            placeholder='Общество с ограниченной ответственностью «...»'
            value={form.fullName}
            onChange={set('fullName')}
            disabled={!canEdit}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <TextInput
            label="Краткое наименование"
            placeholder='ООО «...»'
            value={form.shortName}
            onChange={set('shortName')}
            disabled={!canEdit}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <TextInput label="ИНН" value={form.inn} onChange={set('inn')} disabled={!canEdit} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <TextInput label="ОГРН" value={form.ogrn} onChange={set('ogrn')} disabled={!canEdit} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <TextInput label="КПП" value={form.kpp} onChange={set('kpp')} disabled={!canEdit} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Юридический адрес"
            value={form.legalAddress}
            onChange={set('legalAddress')}
            disabled={!canEdit}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Почтовый адрес"
            description="Если отличается от юридического"
            value={form.postalAddress}
            onChange={set('postalAddress')}
            disabled={!canEdit}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <AddressBreakdownFields
            value={addrParts}
            onChange={canEdit ? setAddrParts : () => undefined}
            hint="Нужна для ЭТрН — юридический адрес уходит в XML структурированно (АдрРФ)."
          />
        </Grid.Col>
      </Grid>

      <Divider my="sm" />
      <Title order={5}>Контакты и руководитель</Title>
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Контактный email"
            value={form.contactEmail}
            onChange={set('contactEmail')}
            disabled={!canEdit}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Телефон"
            value={form.contactPhone}
            onChange={set('contactPhone')}
            disabled={!canEdit}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="ФИО руководителя"
            placeholder="Иванов Иван Иванович"
            value={form.directorName}
            onChange={set('directorName')}
            disabled={!canEdit}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Должность"
            placeholder="Генеральный директор"
            value={form.directorPosition}
            onChange={set('directorPosition')}
            disabled={!canEdit}
          />
        </Grid.Col>
      </Grid>

      <Divider my="sm" />
      <Title order={5}>Банковские реквизиты</Title>
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Наименование банка"
            value={form.bankName}
            onChange={set('bankName')}
            disabled={!canEdit}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="БИК"
            value={form.bankBik}
            onChange={set('bankBik')}
            disabled={!canEdit}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Корреспондентский счёт"
            value={form.bankCorrAccount}
            onChange={set('bankCorrAccount')}
            disabled={!canEdit}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Расчётный счёт"
            value={form.bankAccount}
            onChange={set('bankAccount')}
            disabled={!canEdit}
          />
        </Grid.Col>
      </Grid>

      <Divider my="sm" />
      <Title order={5}>Электронный документооборот</Title>
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Код абонента ЭДО"
            description="Идентификатор в ЭДО.МИГ24 — клиенты видят его и используют для отправки нам документов"
            value={form.edoSubscriberId}
            onChange={set('edoSubscriberId')}
            disabled={!canEdit}
          />
        </Grid.Col>
      </Grid>

      <Divider my="sm" />
      <Title order={5}>Электронная транспортная накладная (ЭТрН)</Title>
      <Switch
        label="ЭТрН — один контейнер на документ"
        description="При создании ЭТрН из поручения с несколькими контейнерами нужно выбрать один; его данные переносятся в ЭТрН, а номер ЭТрН = номер контейнера без букв (MSCU1234567 → 1234567)."
        checked={oneContainer}
        onChange={(e) => setOneContainer(e.currentTarget.checked)}
        disabled={!canEdit}
      />
    </>
  );

  return (
    <Box>
      <Title order={3} mb="md">
        Реквизиты экспедитора
      </Title>

      {!canEdit && (
        <Alert
          icon={<IconShieldLock size={16} />}
          color="gray"
          mb="md"
          title="Только для просмотра"
        >
          Изменять реквизиты может только администратор системы.
        </Alert>
      )}

      <Paper withBorder p="lg" radius="md">
        {canEdit ? (
          <form onSubmit={onSubmit}>
            <Stack gap="md">
              {fields}
              {error && (
                <Alert color="red" icon={<IconInfoCircle size={16} />}>
                  {error}
                </Alert>
              )}
              {saved && (
                <Alert color="green" icon={<IconDeviceFloppy size={16} />}>
                  Реквизиты сохранены.
                </Alert>
              )}
              <Group justify="flex-end">
                <Button type="submit" loading={saving} leftSection={<IconDeviceFloppy size={16} />}>
                  Сохранить
                </Button>
              </Group>
            </Stack>
          </form>
        ) : (
          <Stack gap="md">{fields}</Stack>
        )}
      </Paper>

      {dto?.updatedAt && (
        <Text size="xs" c="dimmed" mt="sm">
          Обновлено: {new Date(dto.updatedAt).toLocaleString('ru-RU')}
          {dto.updatedByUserEmail ? ` · ${dto.updatedByUserEmail}` : ''}
        </Text>
      )}
    </Box>
  );
}
